import { ReactComponent as SearchIcon } from "assets/icons/search.svg";
import { handlePageClick, handlePageSizeChange } from "helpers/PaginationHelper";
import {
    Button,
    Checkbox,
    Field,
    Input,
    Loading,
    Modal,
    ModalFooter,
    ModalHeader,
    NoResults,
    OptionType,
    Pagination,
    useDebounced,
    useGetFilteredUserAccountsQuery,
    UserAccountActiveStatus,
} from "modules/common";
import { mapUserName } from "modules/dashboard";
import React, { useContext, useEffect, useState } from "react";
import { SendNotificationFilters } from "./SendNotificationFilters";
import { AccessibilityContext } from "views/AccessibilityContext";

const PAGE_NEIGHBOURS = 3;
const INPUT_DEBOUNCE_TIME = 500; //miliseconds

interface Props {
    isActive: boolean;
    toggleModal: () => void;
    selectedUsers: string[];
    setSelectedUsers: React.Dispatch<React.SetStateAction<string[]>>;
    currentPage: number;
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
    pageSize: OptionType<string>;
    setPageSize: React.Dispatch<React.SetStateAction<OptionType<string>>>;
    roleFilter: number[];
    setRoleFilter: React.Dispatch<React.SetStateAction<number[]>>;
    countyFilter: number[];
    setCountyFilter: React.Dispatch<React.SetStateAction<number[]>>;
    cityFilter: number[];
    setCityFilter: React.Dispatch<React.SetStateAction<number[]>>;
    registryFilter: string[];
    setRegistryFilter: React.Dispatch<React.SetStateAction<string[]>>;
    counties?: OptionType<number | null>[];
    isLoadingCounties: boolean;
}

export const NotificationUsersModal = ({
    isActive,
    toggleModal,
    selectedUsers,
    setSelectedUsers,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    roleFilter,
    setRoleFilter,
    countyFilter,
    setCountyFilter,
    cityFilter,
    registryFilter,
    setRegistryFilter,
    setCityFilter,
    counties,
    isLoadingCounties,
}: Props) => {
    const [localSelectedUsers, setLocalSelectedUsers] = useState(selectedUsers);
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounced(searchTerm, INPUT_DEBOUNCE_TIME);

    useEffect(() => {
        if (isActive) setLocalSelectedUsers(selectedUsers);
    }, [selectedUsers, isActive]);

    const { data: filteredUsers, isFetching: isLoadingAccounts } = useGetFilteredUserAccountsQuery(
        {
            pageNumber: currentPage,
            pageSize: parseInt(pageSize.value),
            search: debouncedSearchTerm,
            registryFilter: registryFilter,
            userRoleFilter: roleFilter,
            countyFilter: countyFilter,
            citiesMunicipalityFilter: cityFilter,
            statusFilter: [UserAccountActiveStatus.Active],
            measureFilter: [],
            operationFilter: [],
            entryYearFilter: [],
            isDoneFilter: [],
        },
        isActive
    );

    const count = filteredUsers?.userAccountCount ?? 0;

    const addRemoveAllUsers = (event: React.ChangeEvent<HTMLInputElement>) => {
        const allUsers = filteredUsers?.userAccountList.map<string>(i => i.uid) ?? [];

        if (event.target.checked) {
            const merged = [
                ...localSelectedUsers,
                ...allUsers.filter(
                    uid => !localSelectedUsers.some(existingUid => existingUid === uid)
                ),
            ];
            setLocalSelectedUsers(merged);
            return;
        }

        const filteredOut = localSelectedUsers.filter(
            existingUid => !allUsers.some(uid => uid === existingUid)
        );
        setLocalSelectedUsers(filteredOut);
    };

    const addRemoveUser = (uid: string) => {
        const selectedUser = filteredUsers?.userAccountList.find(i => i.uid === uid);
        if (!selectedUser) return;

        setLocalSelectedUsers(prev => {
            const isAlreadySelected = prev.some(uid => uid === selectedUser.uid);

            if (isAlreadySelected) return prev.filter(uid => uid !== selectedUser.uid);

            return [...prev, selectedUser.uid];
        });
    };

    const displayUsers = () => {
        return (
            <div className="f-c g-8">
                {filteredUsers?.userAccountList.map(value => {
                    return (
                        <div key={value.oib}>
                            <Checkbox
                                type="checkbox"
                                isReversed
                                value={value?.uid}
                                isChecked={localSelectedUsers.some(i => i === value?.uid)}
                                onChange={() => addRemoveUser(value?.uid)}
                                htmlFor={value?.uid}
                                text={<div>{mapUserName(value)}</div>}
                            />
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderComponents = () => {
        if (isLoadingAccounts) return <Loading />;
        else if (filteredUsers) return displayUsers();
        else return <NoResults />;
    };

    const closeModal = () => {
        toggleModal();
        setSearchTerm("");
        setCurrentPage(1);
        setLocalSelectedUsers([]);
    };

     const { lowVision } = useContext(AccessibilityContext);

    return (
        <Modal
            maintainWidth={40}
            isActive={isActive}
            onClose={closeModal}
            modalHeader={<ModalHeader onClose={closeModal} title="Lista primatelja" />}
            modalFooter={
                <ModalFooter>
                    <div className="f-r f-1 g-24">
                        <Button
                            type="button"
                            size="small"
                            variant="primary-ghost"
                            lowVision={lowVision}
                            width="wide"
                            onClick={() => setLocalSelectedUsers([])}
                        >
                            PONIŠTI RUČNI ODABIR
                        </Button>

                        <div className="f-a-x-r">
                            <Button
                                type="button"
                                size="small"
                                variant="primary"
                                lowVision={lowVision}
                                width="standard"
                                onClick={() => {
                                    setSelectedUsers(localSelectedUsers);
                                    closeModal();
                                }}
                            >
                                SPREMI
                            </Button>
                        </div>
                    </div>
                </ModalFooter>
            }
        >
            <div className="communication-user-modal f-c g-24">
                <SendNotificationFilters
                    countyFilter={countyFilter}
                    setCountyFilter={setCountyFilter}
                    settlementFilter={cityFilter}
                    setCityFilter={setCityFilter}
                    roleFilter={roleFilter}
                    setRoleFilter={setRoleFilter}
                    setCurrentPage={setCurrentPage}
                    registryFilter={registryFilter}
                    setRegistryFilter={setRegistryFilter}
                    counties={counties}
                    isLoadingCounties={isLoadingCounties}
                />

                <div>
                    <Field name="PRETRAGA">
                        <Input
                            name="naziv"
                            id="naziv"
                            type="text"
                            size="medium"
                            placeholder="Pretraži..."
                            onChange={e => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                            leftIcon={<SearchIcon />}
                        />
                    </Field>

                    <hr className="separator mt-12 mb-12" />

                    <Field name="ODABIR KORISNIKA">
                        <div className="f-r g-24 f-ai-c">
                            <Checkbox
                                className="f-1"
                                isReversed
                                type="checkbox"
                                isChecked={
                                    filteredUsers?.userAccountList.length === 0
                                        ? false
                                        : Boolean(
                                              filteredUsers?.userAccountList.every(item =>
                                                  localSelectedUsers.some(i => i === item.uid)
                                              )
                                          )
                                }
                                isPartial={Boolean(
                                    filteredUsers?.userAccountList.some(item =>
                                        localSelectedUsers.some(i => i === item.uid)
                                    )
                                )}
                                onChange={addRemoveAllUsers}
                                htmlFor="addRemoveRecipients"
                                text="Odaberi sve prikazane korisnike"
                            />
                            <div>
                                {isLoadingAccounts ? (
                                    <Loading size="small" />
                                ) : (
                                    `Odabrano ${localSelectedUsers.length}/${
                                        filteredUsers?.userAccountCount ?? 0
                                    }`
                                )}
                            </div>
                        </div>
                    </Field>

                    <hr className="separator mt-12 mb-12" />

                    <div>
                        {renderComponents()}

                        <hr className="separator mt-12 mb-12" />

                        <Pagination
                            currentPage={currentPage}
                            numberOfPages={Math.ceil(count / parseInt(pageSize?.value))}
                            pageNeighbours={PAGE_NEIGHBOURS}
                            pageSize={pageSize}
                            onPageSizeChange={e =>
                                handlePageSizeChange(e, setPageSize, setCurrentPage)
                            }
                            handlePageClick={e => handlePageClick(e, setCurrentPage)}
                            handlePageSelect={e => setCurrentPage(e)}
                            next={() => setCurrentPage(p => p + 1)}
                            prev={() => setCurrentPage(p => p - 1)}
                            first={() => setCurrentPage(1)}
                            last={() => setCurrentPage(Math.ceil(count / parseInt(pageSize.value)))}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
};
